/**
 * VyasaDiagnostics — full-detail diagnostics for the Vyasa strategy.
 *
 * Surfaces every field from /api/strategies/vyasa/state with operator-grade
 * detail. Sections:
 *   1. Service Health: state, durations, heartbeat, halt reason, suite health
 *   2. Combo Suite: per-combo threshold + model version + status (and a hook
 *      for live decision rows once the backend exposes them)
 *   3. Counters: trades / open / blocked-book / blocked-cap (with WHY tooltips)
 *   4. Open Positions: every column from vyasa_positions (open rows)
 *   5. Closed Trades: every column including peak fav/adv pts
 *   6. Event Stream: severity, message, expandable meta JSON
 *   7. Raw Payload: the JSON envelope itself (collapsed by default)
 */
import React, { useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useVyasaMonitor } from '../vyasa/useVyasaMonitor';

const MONO_FONT = '"JetBrains Mono", ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';

// Suite config — matches DEPLOYMENT_SUITE in vyasa/config.py.
// Listed here so we can show "expected vs loaded" diagnostics even before the
// backend exposes per-combo decision telemetry.
const EXPECTED_SUITE = [
    { combo: 'NIFTY-PE-30m',     threshold: 0.60, universe: 'NIFTY',     side: 'PE', horizonMin: 30 },
    { combo: 'NIFTY-CE-30m',     threshold: 0.70, universe: 'NIFTY',     side: 'CE', horizonMin: 30 },
    { combo: 'NIFTY-CE-15m',     threshold: 0.60, universe: 'NIFTY',     side: 'CE', horizonMin: 15 },
    { combo: 'BANKNIFTY-PE-30m', threshold: 0.70, universe: 'BANKNIFTY', side: 'PE', horizonMin: 30 },
];

// ── Formatting helpers ──────────────────────────────────────────────────────

const formatTs = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-GB', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
};

const formatRelativeAgo = (iso) => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const sec = Math.floor((Date.now() - t) / 1000);
    if (sec < 0) return 'in the future';
    if (sec < 60)         return `${sec}s ago`;
    if (sec < 3600)       return `${Math.floor(sec / 60)}m ${sec % 60}s ago`;
    if (sec < 86400)      return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
    return `${Math.floor(sec / 86400)}d ago`;
};

const formatNum = (v, digits = 2) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return n.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });
};

const formatInt = (v) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return n.toLocaleString('en-IN');
};

const formatCur = (v) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    const sign = n < 0 ? '-' : '';
    return `${sign}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
};

const tonePnl = (v) => (Number(v) > 0 ? 'success' : Number(v) < 0 ? 'error' : 'default');

// ── Sub-components ──────────────────────────────────────────────────────────

const SectionCard = ({ title, eyebrow, description, children, action }) => (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1.75, md: 2.25 }, '&:last-child': { pb: { xs: 1.75, md: 2.25 } } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Box>
                    {eyebrow ? (
                        <Typography
                            variant="overline"
                            sx={{ letterSpacing: '0.14em', fontWeight: 700, color: 'text.secondary' }}
                        >
                            {eyebrow}
                        </Typography>
                    ) : null}
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mt: eyebrow ? 0.25 : 0 }}>
                        {title}
                    </Typography>
                    {description ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 880, lineHeight: 1.55 }}>
                            {description}
                        </Typography>
                    ) : null}
                </Box>
                {action ? <Box>{action}</Box> : null}
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const KvGrid = ({ rows }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
        }}
    >
        {rows.map((row) => (
            <Box
                key={row.label}
                sx={(theme) => ({
                    p: 1.1,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.55)' : 'rgba(248,250,252,0.85)',
                })}
            >
                <Typography
                    variant="overline"
                    sx={{ letterSpacing: '0.12em', color: 'text.secondary', fontWeight: 700, fontSize: '0.66rem' }}
                >
                    {row.label}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        mt: 0.4,
                        fontWeight: 700,
                        fontFamily: row.mono ? MONO_FONT : undefined,
                        fontVariantNumeric: 'tabular-nums',
                        color: row.tone === 'success' ? 'success.main'
                            : row.tone === 'error' ? 'error.main'
                            : row.tone === 'warning' ? 'warning.main'
                            : 'text.primary',
                        wordBreak: 'break-word',
                    }}
                >
                    {row.value}
                </Typography>
                {row.detail ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, display: 'block', lineHeight: 1.4 }}>
                        {row.detail}
                    </Typography>
                ) : null}
            </Box>
        ))}
    </Box>
);

// ── Main view ───────────────────────────────────────────────────────────────

const VyasaDiagnostics = () => {
    const monitor = useVyasaMonitor({ pollIntervalMs: 1000 });
    const { state = {}, legs = [], history = [], historyTotals = {}, events = [], liveUnrealizedFromLegs, fetchedAt, tradingDayStart, connected, error, strategyId, strategyLabel } = monitor;
    const counters = state.counters || {};
    const pnl = state.pnl || {};
    const modelVersions = state.modelVersions || {};

    const liveTotal = (Number(pnl.realized) || 0) + (Number(liveUnrealizedFromLegs ?? pnl.unrealized) || 0);
    const modelsLoaded = Object.keys(modelVersions).length;

    const serviceRows = useMemo(() => [
        { label: 'Strategy ID',        value: strategyId || 'vyasa_v1', mono: true },
        { label: 'Label',              value: strategyLabel || 'Vyasa' },
        { label: 'State',              value: (state.state || '—').toUpperCase(),
          tone: state.state === 'trading' || state.state === 'armed' ? 'success' : state.state === 'halted' ? 'error' : 'default',
          detail: state.stateChangedAt ? `since ${formatRelativeAgo(state.stateChangedAt)}` : null },
        { label: 'Halt Reason',        value: state.haltReason || '—', tone: state.haltReason ? 'error' : 'default' },
        { label: 'Last Heartbeat',     value: formatTs(state.lastHeartbeatAt),
          mono: true, detail: state.lastHeartbeatAt ? formatRelativeAgo(state.lastHeartbeatAt) : null,
          tone: state.lastHeartbeatAt && (Date.now() - new Date(state.lastHeartbeatAt).getTime()) > 60000 ? 'warning' : 'default' },
        { label: 'Trading Day Start',  value: formatTs(tradingDayStart), mono: true },
        { label: 'Payload Fetched',    value: formatTs(fetchedAt),       mono: true,
          detail: connected ? 'poll healthy' : (error ? `error: ${error}` : 'disconnected'),
          tone: connected ? 'success' : 'error' },
        { label: 'Models Loaded',      value: `${modelsLoaded} / ${EXPECTED_SUITE.length}`,
          tone: modelsLoaded === EXPECTED_SUITE.length ? 'success' : 'error',
          detail: 'configured suite has 4 combos' },
    ], [strategyId, strategyLabel, state, tradingDayStart, fetchedAt, connected, error, modelsLoaded]);

    const counterRows = [
        { label: 'Trades Today',       value: formatInt(counters.trades),
          detail: 'open + closed since trading day start' },
        { label: 'Open',               value: `${formatInt(counters.open)} / 35`,
          tone: (counters.open || 0) >= 30 ? 'warning' : 'default',
          detail: 'concurrent positions; hard cap = 35' },
        { label: 'Blocked (book)',     value: formatInt(counters.blockedBook),
          detail: '1-lot-per-key admission rejects (since boot)' },
        { label: 'Blocked (cap)',      value: formatInt(counters.blockedCap),
          detail: '35-cap admission rejects (since boot)' },
        { label: 'Realized P&L',       value: formatCur(pnl.realized),       tone: tonePnl(pnl.realized) },
        { label: 'Unrealized (live)',  value: formatCur(liveUnrealizedFromLegs ?? pnl.unrealized),
          tone: tonePnl(liveUnrealizedFromLegs ?? pnl.unrealized),
          detail: 'sum of mtmPnlNet across open legs (1Hz refresh)' },
        { label: 'Live Total',         value: formatCur(liveTotal),          tone: tonePnl(liveTotal) },
        { label: 'Closed Today',       value: formatInt(historyTotals.closedCount) },
        { label: 'Realized (gross)',   value: formatCur(historyTotals.realizedGross) },
        { label: 'TCA Cost (today)',   value: formatCur(historyTotals.tcaCost),
          detail: 'broker brokerage + slippage proxy' },
    ];

    const comboRows = EXPECTED_SUITE.map((expected) => {
        const versionRaw = modelVersions[expected.combo];
        const loaded = versionRaw !== undefined;
        return {
            ...expected,
            loaded,
            version: versionRaw || '—',
            // Last decision is not yet exposed by the backend; placeholder.
            lastDecision: null,
        };
    });

    return (
        <Stack spacing={2.5}>
            {/* 1. Service health */}
            <SectionCard
                eyebrow="Service Health"
                title="Vyasa runtime + payload freshness"
                description="State machine, halt reasons, heartbeat age, and the freshness of the data this view is showing."
            >
                <KvGrid rows={serviceRows} />
            </SectionCard>

            {/* 2. Combo suite */}
            <SectionCard
                eyebrow="Combo Suite"
                title="4-combo unified-book configuration"
                description="One row per combo with its model version, threshold, and load status. The 'last decision' column will populate once the backend exposes per-combo scoring telemetry."
            >
                <TableContainer>
                    <Table size="small" sx={{ '& td, & th': { fontFamily: MONO_FONT, fontSize: '0.78rem' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Combo</TableCell>
                                <TableCell>Universe</TableCell>
                                <TableCell>Side</TableCell>
                                <TableCell align="right">Horizon</TableCell>
                                <TableCell align="right">Threshold</TableCell>
                                <TableCell>Model Version</TableCell>
                                <TableCell>Loaded</TableCell>
                                <TableCell>Last Decision</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {comboRows.map((c) => (
                                <TableRow key={c.combo} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{c.combo}</TableCell>
                                    <TableCell>{c.universe}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={c.side}
                                            color={c.side === 'CE' ? 'info' : 'warning'}
                                            variant="outlined"
                                            sx={{ fontFamily: MONO_FONT, fontWeight: 700 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{c.horizonMin}m</TableCell>
                                    <TableCell align="right">{c.threshold.toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: c.version === '?' ? 'text.secondary' : 'text.primary' }}>{c.version}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={c.loaded ? 'loaded' : 'missing'}
                                            color={c.loaded ? 'success' : 'error'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        TODO — backend endpoint
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {modelsLoaded < EXPECTED_SUITE.length ? (
                    <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
                        {EXPECTED_SUITE.length - modelsLoaded} combo(s) missing from <code>state.modelVersions</code>.
                        Service may not score signals for those combos.
                    </Alert>
                ) : null}
            </SectionCard>

            {/* 3. Counters */}
            <SectionCard
                eyebrow="Counters"
                title="Position / P&L / blocked-signal counters"
                description="Sourced from the live positions table (open, trades, P&L) and the in-memory PositionBook (blocked counters, persisted at heartbeat cadence)."
            >
                <KvGrid rows={counterRows} />
            </SectionCard>

            {/* 4. Open positions — full detail */}
            <SectionCard
                eyebrow={`Open Positions (${legs.length})`}
                title="Currently-open legs"
                description="Every column from vyasa_positions for rows where exit_ts IS NULL. LTP and MTM refresh ≥ 1 Hz."
            >
                {legs.length === 0 ? (
                    <Alert severity="info" variant="outlined">No open positions.</Alert>
                ) : (
                    <TableContainer sx={{ maxHeight: 480 }}>
                        <Table size="small" stickyHeader sx={{ '& td, & th': { fontFamily: MONO_FONT, fontSize: '0.74rem', whiteSpace: 'nowrap' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Combo</TableCell>
                                    <TableCell>Universe</TableCell>
                                    <TableCell>Side</TableCell>
                                    <TableCell>Symbol</TableCell>
                                    <TableCell align="right">Strike</TableCell>
                                    <TableCell>Expiry</TableCell>
                                    <TableCell align="right">Horizon</TableCell>
                                    <TableCell align="right">Score@Entry</TableCell>
                                    <TableCell align="right">Fold</TableCell>
                                    <TableCell>Entry TS</TableCell>
                                    <TableCell>Planned Exit</TableCell>
                                    <TableCell align="right">Entry ₹</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Lot</TableCell>
                                    <TableCell align="right">LTP</TableCell>
                                    <TableCell>LTP TS</TableCell>
                                    <TableCell align="right">MTM Gross</TableCell>
                                    <TableCell align="right">MTM Net</TableCell>
                                    <TableCell>Order ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {legs.map((leg) => (
                                    <TableRow key={leg.id} hover>
                                        <TableCell>{leg.id}</TableCell>
                                        <TableCell>{leg.comboLabel || '—'}</TableCell>
                                        <TableCell>{leg.universe}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={leg.side} color={leg.side === 'CE' ? 'info' : 'warning'} variant="outlined" />
                                        </TableCell>
                                        <TableCell>{leg.symbol}</TableCell>
                                        <TableCell align="right">{formatNum(leg.strike, 0)}</TableCell>
                                        <TableCell>{leg.expiry || '—'}</TableCell>
                                        <TableCell align="right">{leg.horizonMin}m</TableCell>
                                        <TableCell align="right">{formatNum(leg.scoreAtEntry, 4)}</TableCell>
                                        <TableCell align="right">{leg.fold ?? '—'}</TableCell>
                                        <TableCell>{formatTs(leg.entryTs)}</TableCell>
                                        <TableCell>{formatTs(leg.plannedExitTs)}</TableCell>
                                        <TableCell align="right">{formatNum(leg.entryPrice, 2)}</TableCell>
                                        <TableCell align="right">{leg.qty}</TableCell>
                                        <TableCell align="right">{leg.lotSize}</TableCell>
                                        <TableCell align="right">{formatNum(leg.lastLtp, 2)}</TableCell>
                                        <TableCell>{formatTs(leg.lastLtpTs)}</TableCell>
                                        <TableCell align="right" sx={{ color: tonePnl(leg.mtmPnlGross) === 'success' ? 'success.main' : tonePnl(leg.mtmPnlGross) === 'error' ? 'error.main' : 'text.primary' }}>
                                            {formatCur(leg.mtmPnlGross)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: tonePnl(leg.mtmPnlNet) === 'success' ? 'success.main' : tonePnl(leg.mtmPnlNet) === 'error' ? 'error.main' : 'text.primary', fontWeight: 700 }}>
                                            {formatCur(leg.mtmPnlNet)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{leg.entryOrderId || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </SectionCard>

            {/* 5. Closed trades — full detail */}
            <SectionCard
                eyebrow={`Closed Trades (${history.length})`}
                title="Closed trades today"
                description="Every column from vyasa_positions for rows where exit_ts IS NOT NULL — including peak favorable / adverse excursion in points and exit reason."
            >
                {history.length === 0 ? (
                    <Alert severity="info" variant="outlined">No closed trades yet today.</Alert>
                ) : (
                    <TableContainer sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader sx={{ '& td, & th': { fontFamily: MONO_FONT, fontSize: '0.74rem', whiteSpace: 'nowrap' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Combo</TableCell>
                                    <TableCell>Side</TableCell>
                                    <TableCell>Symbol</TableCell>
                                    <TableCell align="right">Strike</TableCell>
                                    <TableCell>Expiry</TableCell>
                                    <TableCell align="right">Score@Entry</TableCell>
                                    <TableCell align="right">Fold</TableCell>
                                    <TableCell>Entry → Exit</TableCell>
                                    <TableCell align="right">Entry ₹</TableCell>
                                    <TableCell align="right">Exit ₹</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">PnL Gross</TableCell>
                                    <TableCell align="right">PnL Net</TableCell>
                                    <TableCell align="right">TCA</TableCell>
                                    <TableCell align="right">Peak Fav (pts)</TableCell>
                                    <TableCell align="right">Peak Adv (pts)</TableCell>
                                    <TableCell>Exit Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((t) => (
                                    <TableRow key={t.id} hover>
                                        <TableCell>{t.id}</TableCell>
                                        <TableCell>{t.comboLabel || '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={t.side} color={t.side === 'CE' ? 'info' : 'warning'} variant="outlined" />
                                        </TableCell>
                                        <TableCell>{t.symbol}</TableCell>
                                        <TableCell align="right">{formatNum(t.strike, 0)}</TableCell>
                                        <TableCell>{t.expiry || '—'}</TableCell>
                                        <TableCell align="right">{formatNum(t.scoreAtEntry, 4)}</TableCell>
                                        <TableCell align="right">{t.fold ?? '—'}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>
                                            {formatTs(t.entryTs)} → {formatTs(t.exitTs)}
                                        </TableCell>
                                        <TableCell align="right">{formatNum(t.entryPrice, 2)}</TableCell>
                                        <TableCell align="right">{formatNum(t.exitPrice, 2)}</TableCell>
                                        <TableCell align="right">{t.qty}</TableCell>
                                        <TableCell align="right" sx={{ color: tonePnl(t.pnlGross) === 'success' ? 'success.main' : tonePnl(t.pnlGross) === 'error' ? 'error.main' : 'text.primary' }}>
                                            {formatCur(t.pnlGross)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: tonePnl(t.pnlNet) === 'success' ? 'success.main' : tonePnl(t.pnlNet) === 'error' ? 'error.main' : 'text.primary', fontWeight: 700 }}>
                                            {formatCur(t.pnlNet)}
                                        </TableCell>
                                        <TableCell align="right">{formatCur(t.tcaTotal)}</TableCell>
                                        <TableCell align="right">{formatNum(t.peakFavPts, 2)}</TableCell>
                                        <TableCell align="right">{formatNum(t.peakAdvPts, 2)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={t.exitReason || 'exit'}
                                                variant="outlined"
                                                sx={{ fontFamily: MONO_FONT, fontWeight: 700 }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </SectionCard>

            {/* 6. Event stream with meta */}
            <SectionCard
                eyebrow={`Events (${events.length})`}
                title="Vyasa journal — full events with meta"
                description="Newest first. Click an event to expand its meta JSON payload (boot details, halt reasons, position-book diagnostics)."
            >
                {events.length === 0 ? (
                    <Alert severity="info" variant="outlined">No events yet today.</Alert>
                ) : (
                    <Stack spacing={0.75}>
                        {events.slice(0, 100).map((ev) => (
                            <Accordion
                                key={ev.id}
                                disableGutters
                                variant="outlined"
                                sx={{ borderRadius: 1.25, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
                                        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, color: 'text.secondary', minWidth: 100 }}>
                                            {formatTs(ev.occurredAt)}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={ev.severity || 'info'}
                                            color={ev.severity === 'error' ? 'error' : ev.severity === 'warn' ? 'warning' : 'default'}
                                            variant="outlined"
                                            sx={{ fontFamily: MONO_FONT, fontWeight: 700, minWidth: 60 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: MONO_FONT, fontSize: '0.78rem', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {ev.message}
                                        </Typography>
                                        {ev.meta ? (
                                            <Chip size="small" label="meta" variant="outlined" sx={{ fontSize: '0.64rem' }} />
                                        ) : null}
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {ev.meta ? (
                                        <Box
                                            component="pre"
                                            sx={(theme) => ({
                                                m: 0,
                                                p: 1.25,
                                                fontFamily: MONO_FONT,
                                                fontSize: '0.74rem',
                                                lineHeight: 1.55,
                                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.95)',
                                                borderRadius: 1,
                                                overflowX: 'auto',
                                            })}
                                        >
                                            {JSON.stringify(ev.meta, null, 2)}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">No meta payload.</Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                        {events.length > 100 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                                Showing newest 100 of {events.length} events.
                            </Typography>
                        ) : null}
                    </Stack>
                )}
            </SectionCard>

            {/* 7. Raw payload (for debugging / verifying field shape) */}
            <SectionCard
                eyebrow="Raw Payload"
                title="GET /api/strategies/vyasa/state envelope"
                description="The exact JSON the frontend just received, for verifying field shapes / debugging the wire format. Updates every poll (1 Hz)."
            >
                <RawPayloadAccordion monitor={monitor} />
            </SectionCard>
        </Stack>
    );
};

const RawPayloadAccordion = ({ monitor }) => {
    const { connected, error, refresh, ...payload } = monitor; // eslint-disable-line no-unused-vars
    const json = useMemo(() => {
        try {
            return JSON.stringify(payload, null, 2);
        } catch (_) {
            return '/* failed to stringify payload */';
        }
    }, [payload]);

    return (
        <Accordion disableGutters variant="outlined" sx={{ borderRadius: 1.25, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontFamily: MONO_FONT, fontSize: '0.78rem' }}>
                    Show JSON ({json.length.toLocaleString()} chars)
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Box
                    component="pre"
                    sx={(theme) => ({
                        m: 0,
                        p: 1.5,
                        fontFamily: MONO_FONT,
                        fontSize: '0.72rem',
                        lineHeight: 1.5,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.7)' : 'rgba(248,250,252,0.95)',
                        borderRadius: 1,
                        maxHeight: 480,
                        overflow: 'auto',
                    })}
                >
                    {json}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default VyasaDiagnostics;
